import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
import { IanaZoneSchema } from '../common/zone.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
import { phoneE164Field } from '../common/phone.js';
import { ResponsibilityLevelSchema } from '../responsibilities/levels.js';
// Request schemas for the users feature (globally-pooled entity per
// CONVENTIONS §Entity categories). Two trust zones:
//   - project (`/api/projects/:projectId/users/*`): list / search-pool /
//     associate / create-and-associate / edit-in-project / remove / disable.
//   - platform (`/api/platform/users/*`, sysadmin): cross-project create.
//
// String `.max(N)` matches the DB column exactly (users.username varchar(100),
// names varchar(64), email varchar(128); CONVENTIONS §Schemas).
//
// THE CREDENTIAL/CHANNEL MODEL (ADR-003 §Federated identity): `username` is the
// identity; CREDENTIALS are a password, SSO, or none; email · phone · Telegram
// are CHANNELS — all optional, never credentials (email doubles as a login
// identifier and a link-delivery route). Project create therefore asks ONE
// question — `web_access`: 'channel' (works through the bot; no credential is
// minted) or 'invite' (a single-use SET-YOUR-OWN-PASSWORD invitation is minted;
// delivered by email when present · the linked Telegram chat when linked · and
// ALWAYS returned once as a copy-link). Nobody ever types a password for someone
// else; must_change_password never rides this path (its residue is the bootstrap
// sysadmin + CLI recovery). An administrator membership REQUIRES 'invite'
// (admin work lives on the web; on-behalf strips admin powers by design).
// ── the scope sections — the scope editor's three sections (ADR-050 §3) ──────
// Sites: "All sites" (the whole tree — every zone, cluster and site; needs a giver
// who holds all sites) or the picks — zones, clusters and extra sites (the server
// keeps the HIGHEST pick: a zone absorbs its clusters and sites, a cluster its
// sites, and says so on the audit line). Depots and Vehicle pools: "All …" (the
// wildcard row) or the ids. All three on = the whole project (scope_mode='all',
// stored as nothing else); the pools section is ignored while the vehicles
// module is off. Every pick is own-scope-bounded at the write, and a giver edits
// only inside their own scope — a person's grants outside it are kept (ADR-050 §1).
const idList = z.array(z.string().min(1).max(16)).max(500).default([]);
export const SitesScopeSectionSchema = z
    .object({
    all: z.boolean().default(false),
    zone_ids: z.array(z.number().int().positive()).max(500).default([]),
    cluster_ids: z.array(z.number().int().positive()).max(500).default([]),
    site_ids: idList,
})
    .strict();
export const EntityScopeSectionSchema = z.object({ all: z.boolean().default(false), ids: idList }).strict();
export const ScopeSectionsSchema = z
    .object({
    sites: SitesScopeSectionSchema.default({ all: false, zone_ids: [], cluster_ids: [], site_ids: [] }),
    depots: EntityScopeSectionSchema.default({ all: false, ids: [] }),
    vehicle_pools: EntityScopeSectionSchema.default({ all: false, ids: [] }),
    /** Team scope (ADR-083 §4) — read only while the field-force module is on; a team's id, never a membership. */
    teams: EntityScopeSectionSchema.default({ all: false, ids: [] }),
})
    .strict();
// The scope WRITE — the sections and the un-backing consent (ADR-052 Direction 2): the
// standalone scope route's body, and the block a membership door carries. If reducing
// scope would un-back chains the caller CAN clear (own-scope + level-set), the write is
// rejected (SCOPE_CHANGE_UNWINDS, carrying the chains) so the UI can confirm; the operator
// re-sends confirm_unwind:true → those chains drop in the same tx. If any would-be-orphaned
// chain is NOT clearable, the write is BLOCKED (SCOPE_CHANGE_BLOCKED) regardless — foreign
// accountability is never touched. Surfaced proactively via the preview, so the editor
// blocks before save (ADR-052 §5).
export const ScopeWriteSchema = ScopeSectionsSchema.extend({ confirm_unwind: z.boolean().default(false) }).strict();
// ── shared membership shape (admin XOR role, mirrors chk_upr_admin_xor_role) ──
// The membership DOORS (create · associate · edit-access) may carry a `scope`
// block — the scope editor's sections, written in the same transaction under the
// scope verb's own gate and audited under one correlation (ADR-063's composition
// grammar; ADR-050 §2). Without it, a new non-admin member starts with NO scope
// (ADR-050 §Consequences — the ONE default), and an edit preserves what there is.
// An administrator carries no role and no scope.
const membershipFields = {
    is_project_admin: z.boolean().default(false),
    role_id: z.number().int().positive().nullable().optional(),
    scope: ScopeWriteSchema.optional(),
};
// Enforces the same coherence the DB CHECK does: a project_admin carries no role (and no
// scope — admins see everything by definition); a non-admin must carry one.
function refineMembership(v, ctx) {
    if (v.is_project_admin) {
        if (v.role_id != null) {
            ctx.addIssue({ code: 'custom', path: ['role_id'], message: 'project_admin membership must not carry a role' });
        }
        if (v.scope !== undefined) {
            ctx.addIssue({ code: 'custom', path: ['scope'], message: 'SCOPE_NOT_APPLICABLE' });
        }
    }
    else {
        if (v.role_id == null) {
            ctx.addIssue({ code: 'custom', path: ['role_id'], message: 'role_id is required for a non-admin membership' });
        }
    }
}
// ── shared global-identity fields (create-and-associate + platform create) ──
// No password here: the create wizard mints INVITATIONS, never passwords; the
// platform create (below) declares its own password field explicitly per-schema
// (never via spread-override — a key-order change must not flip a contract).
//
// The username SLUG rule (the human doors only): lowercase letters/digits with
// . _ - inside, alphanumeric at both ends, 3–100 — and never '@' (login resolves
// "identifier contains @" as email, so an @-bearing username could never sign in
// by username). Deliberately NOT a DB CHECK: SSO JIT mints email-shaped
// usernames by design (the collision semantics the sso service documents), so
// the slug rule is this schema's door, not a table invariant.
export const USERNAME_SLUG_RE = /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/;
const identityFields = {
    username: z.string().trim().min(3).max(100).regex(USERNAME_SLUG_RE, 'USERNAME_INVALID'),
    first_name: z.string().trim().min(1).max(64),
    last_name: z.string().trim().min(1).max(64),
    email: z.string().trim().email().max(128).nullable().optional(),
    phone_e164: phoneE164Field,
    language: LanguageSchema.optional(),
    // The person's zone (ADR-082 §3) — written ONCE here (the project wizard pre-fills the project's; the platform
    // page asks), edited later in the profile. Required: a person always has one; nothing reads a browser's.
    timezone_iana: IanaZoneSchema,
};
// ── POST /api/projects/:projectId/users — create-and-associate ──────────────
// Creates the global users row AND the user_project_roles link in one call
// (CONVENTIONS §Globally-pooled). `web_access` is the one credential question
// (see header); email stays optional in BOTH modes (the invite's copy-link
// delivery needs nothing on file).
export const CreateUserRequestSchema = z
    .object({
    ...identityFields,
    web_access: z.enum(['channel', 'invite']),
    // The second access square: also mint the project's CHAT invite with the
    // account (default off — old callers unchanged). Orchestrated server-side so
    // the invitation EMAIL can carry the chat link + code in one message; a
    // missing bot or a courier failure never fails the create (the response's
    // chat_invite is simply null).
    telegram_invite: z.boolean().default(false),
    ...membershipFields,
})
    .strict()
    .superRefine(refineMembership)
    .superRefine((v, ctx) => {
    // An administrator needs web access: admin surfaces live on the web, and the
    // bot's on-behalf path strips admin powers by design — a channel-only admin
    // could use their adminhood nowhere (architect-ruled).
    if (v.is_project_admin && v.web_access !== 'invite') {
        ctx.addIssue({ code: 'custom', path: ['web_access'], message: 'ADMIN_NEEDS_WEB_ACCESS' });
    }
});
// ── POST /api/projects/:projectId/users/associate — link an existing user ───
export const AssociateUserRequestSchema = z
    .object({
    user_id: z.string().trim().min(1).max(16),
    ...membershipFields,
})
    .strict()
    .superRefine(refineMembership);
// ── PATCH /api/projects/:projectId/users/:userId — edit access ───────────────
// The membership (role / admin flag) and, when sent, the person's scope (the `scope`
// block — the same sections as the scope write, the same gates, one transaction); a role
// edit without it PRESERVES the member's scope, an admin made a member starts with none.
// Global identity (name / email / phone) is edited by the user at /api/auth/me/profile
// or a sysadmin surface — never from a project URL.
export const EditUserInProjectRequestSchema = z
    .object({ ...stampedBody, ...membershipFields })
    .strict()
    .superRefine(refineMembership);
// ── DELETE /api/projects/:projectId/users/:userId — the LEAVE (ADR-081 §4) ──
// The membership is ENDED, never deleted; the person's grants, chains, follows
// and saved views end with it, explicitly, and the project's tokens are revoked.
// `confirm_gaps=true`: the caller has seen the chains the person still holds here
// (the impact read) and leaves the gaps the handover did not fill — without it a
// person holding a chain is refused `409 CHAINS_END` naming the places (the scope
// editor's confirm-unwind grammar, ADR-052 §5).
// `disable=true` is the sole-membership "remove & disable" flow: the global
// account is deactivated in the SAME transaction as the leave, so a failure
// can't leave it disabled-but-still-a-member. Only valid when this is the user's
// last ACTIVE membership (else USER_IN_MULTIPLE_PROJECTS — you'd disable an
// account still active elsewhere). Query enums (not z.coerce.boolean — that reads
// "false" as true).
const queryFlag = z.enum(['true', 'false']).default('false').transform((v) => v === 'true');
export const RemoveUserQuerySchema = z.object({ disable: queryFlag, confirm_gaps: queryFlag }).strict();
// ── POST /api/projects/:projectId/users/:userId/handover — the handover ───────
// ONE tool for the disable warning and the leave dialog (ADR-081 §5): per place
// the person holds, a successor who takes their position in the chain, or none —
// the person leaves the chain and the next moves up, or a gap remains. Each
// place rides `setAssignmentChain` (the one choke point: own-scope · level-set ·
// the managed-roles bound · the backing consent — `grant_backing` as on the
// assign write, ADR-052 §5). All places in one transaction.
export const HandoverPlaceSchema = z
    .object({
    responsibility_id: z.number().int().positive(),
    level: ResponsibilityLevelSchema,
    /** The chain's place as the person's chains read names it (`scope_id`). */
    scope_id: z.string().trim().min(1).max(20),
    /** The successor — null: the person simply leaves this chain. */
    successor_id: z.string().trim().min(1).max(16).nullable(),
})
    .strict();
export const HandoverRequestSchema = z
    .object({ places: z.array(HandoverPlaceSchema).min(1).max(100), grant_backing: z.boolean().default(false) })
    .strict();
// ── GET /api/projects/:projectId/users/search?q=… — global-pool search ──────
export const SearchUsersQuerySchema = z
    .object({ q: z.string().trim().min(1).max(100) })
    .strict();
// ── PUT /api/projects/:projectId/users/:userId/scope — set a member's scope ─────
// The scope write (the one people key: whom-bound by the managed roles, own-scope-bounded
// per section — ADR-050 §1 · §3): the three sections above, for a non-admin member
// (admins carry scope_mode=NULL by chk_upr_admin_xor_role). Empty sections = sees nothing
// (valid — "No scope yet"). The server keeps the highest pick, derives the whole project
// from all three "All …" switches, and keeps a person's grants outside the giver's reach.
export const SetUserScopeRequestSchema = ScopeWriteSchema.extend(stampedBody);
// ── POST /api/projects/:projectId/users/:userId/scope/preview ───────────────
// Read-only impact preview for the coverage editor (ADR-052 §5 proactive surfacing):
// the proposed scope in, the chains this user would lose backing for out — each flagged
// `clearable` — so the editor surfaces a blocked removal BEFORE save. Same body as the write.
export const PreviewUserScopeRequestSchema = SetUserScopeRequestSchema;
// ── GET /api/projects/:projectId/users/export — current-view export ─────────
// Mirrors the list's URL view-state so the file IS what the user sees (ADR-045
// — export reflects the current filtered view). `role` (a role's id, or `admin`
// for the project administrators) and `status` arrive comma-separated (the
// multi-value filters); `q` is the free-text search; `lang`
// is the UI language driving header/sheet localization. Visibility (managed_roles
// narrowing) is always applied server-side on top of these — export is
// self-bounding. Strict project isolation; no pagination (full view).
const csvFilter = z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(z.string().max(64)).max(50).optional());
export const ExportUsersQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    role: csvFilter,
    status: csvFilter,
    // The Scope filter's values (ScopeKind: admin · whole · specific · none) — the list's column words.
    scope: csvFilter,
    q: z.string().trim().max(100).optional(),
})
    .strict();
// ── POST /api/platform/users — sysadmin cross-project create (no membership) ─
export const PlatformCreateUserRequestSchema = z
    .object({
    ...identityFields,
    // The platform surface keeps its admin-set password shape until its F3 rework
    // converts it to invitations (carded); declared HERE, never spread-overridden.
    password: z.string().min(8).max(128),
    is_sysadmin: z.boolean().default(false),
})
    .strict();
// ── POST /api/platform/users/:userId/place — place a pending user (ADR-054) ─────
// Assign a pending_activation user to a project on a role + activate them (the SSO
// no-match fallback resolved by the sysadmin). role_id must be a role in project_id.
export const PlatformPlaceUserRequestSchema = z
    .object({
    project_id: z.string().trim().min(1).max(16),
    role_id: z.number().int().positive(),
})
    .strict();
